import { BusinessService } from "@/services/business/BusinessService";
import { AuthContext } from "@/context/AuthContext";
import { LocalStorageKeyEnum } from "@/models/enum/LocalStorageKeyEnum";
import { Storage } from "@/models/facade/Storage";
import { AuthRestService } from "@/services/rest/AuthRestService";
import { OAuth2Token } from "@/dtos/OAuth2Token";
import store from "@/store";
import { AuthInfoModel } from "@/models/AuthInfoModel";
import { CustomerStore } from "@/main";
import { CustomerContext } from "@/context/CustomerContext";
import { TokenExchangeRestService } from "@/services/rest/auth-admin/TokenExchangeRestService";
import { TokenExchangeTypeEnum } from "@/api/enums/token-exchange-type-enum";
import I18n from "@/modules/i18n";
import { JwtHelper } from "@/helpers/JwtHelper";

export class AuthService extends BusinessService
{
    private timer: any;
    private isStorageListenerRegistered: boolean = false;

    public async initializeAuth(): Promise<void>
    {
        this.registerStorageListener();

        const oauth2TokenString = Storage.local.get(LocalStorageKeyEnum.OAUTH2_TOKEN, null);

        if (oauth2TokenString === null) {
            return;
        }

        let oauth2Token;

        try {
            const parsed = JSON.parse(oauth2TokenString) as OAuth2Token;
            oauth2Token = new OAuth2Token(
                parsed.idToken,
                parsed.refreshToken,
                parsed.expiresIn,
                parsed.expiresAt,
            );
        } catch (e) {
            Storage.local.remove(LocalStorageKeyEnum.OAUTH2_TOKEN);

            await this.login();

            return;
        }

        AuthContext.setOAuth2Token(oauth2Token);

        if (AuthContext.isUserMode() && AuthContext.getOAuth2TokenOrFail().isExpired()) {
            await this.handleUserModeExpired();

            return;
        }

        if (AuthContext.getOAuth2TokenOrFail().isExpired()) {
            // Tries to refresh auth, automatically redirects to logout on failure
            await this.refreshAuth();
        }

        const currentCustomerCode = Storage.local.get(
            LocalStorageKeyEnum.CURRENT_CUSTOMER_CODE
        );
        const pharmacyCode = Storage.local.get(
            LocalStorageKeyEnum.CURRENT_PHARMACY_CODE
        );

        store.commit(
            "setAuthInfo",
            new AuthInfoModel(
                currentCustomerCode,
                AuthContext.getOAuth2TokenOrFail().idToken,
                false
            )
        );
        await CustomerStore.setPharmacyCode(pharmacyCode);
        CustomerContext.setCustomerCode(currentCustomerCode);

        this.setRefreshAuthTimer(oauth2Token);
    }

    public async login(): Promise<void>
    {
        const loginUrl = await AuthRestService.getInstance().getLoginUrl();

        window.location.replace(loginUrl);
    }

    public async logout(): Promise<void>
    {
        AuthContext.clear();

        this.clearStorageData();
        Storage.local.remove(LocalStorageKeyEnum.USER_MODE_CUSTOMER_CODES);

        await store.dispatch("autoLogout");

        this.clearTimer();

        const logoutUrl = await AuthRestService.getInstance().getLogoutUrl();

        window.location.replace(logoutUrl);
    }

    public async handleOAuth2Callback(code: string, state: string): Promise<void>
    {
        // This flow skips initializeAuth(), so the listener is registered here as well
        this.registerStorageListener();

        let token;

        try {
            token = await AuthRestService.getInstance().getTokenByCode(code, state);
        } catch (error) {
            alert("Could not initialize authorization, please re-login.");

            await this.login();
        }

        Storage.local.set(LocalStorageKeyEnum.OAUTH2_TOKEN, JSON.stringify(token));
        AuthContext.setOAuth2Token(token);

        await store.dispatch("handleOAuth2V2Callback", {token: token});

        if (AuthContext.isDemoUser()) {
            AuthContext.toggleIsDpUser(); // By default toggle the isDpUser for the demo user
        }

        this.setRefreshAuthTimer(token);
    }

    public async startUserMode(customerCodes: string[]): Promise<void>
    {
        if (!AuthContext.isDpUser(true) || AuthContext.isUserMode()) {
            throw new Error('User mode can only be started by a DP user that is not in user mode.');
        }

        const response = await TokenExchangeRestService.getInstance().exchangeToken({
            exchange_type_code: TokenExchangeTypeEnum.RESTRICTED_ADMIN,
            customer_codes: customerCodes,
        });

        if (typeof response?.id_token !== 'string' || response.id_token === '' || !(response.expires_in > 0)) {
            throw new Error('The token exchange returned no valid token, user mode was not started.');
        }

        Storage.local.set(LocalStorageKeyEnum.USER_MODE_CUSTOMER_CODES, JSON.stringify(customerCodes));
        Storage.local.set(
            LocalStorageKeyEnum.OAUTH2_TOKEN,
            JSON.stringify(new OAuth2Token(response.id_token, '', response.expires_in))
        );
        Storage.local.set(LocalStorageKeyEnum.CURRENT_CUSTOMER_CODE, customerCodes[0].trim());
        Storage.local.remove(LocalStorageKeyEnum.CURRENT_PHARMACY_CODE);

        this.clearTimer();

        window.location.assign('/home');
    }

    /**
     * The customer codes of the last successful user-mode start, used to prefill the user-mode dialog.
     * Missing or unreadable stored data gives an empty list.
     */
    public getLastUserModeCustomerCodes(): string[]
    {
        const storedValue = Storage.local.get(LocalStorageKeyEnum.USER_MODE_CUSTOMER_CODES, null);

        if (storedValue === null) {
            return [];
        }

        try {
            const parsed: unknown = JSON.parse(storedValue);

            return Array.isArray(parsed) ? parsed.filter((code: unknown) => typeof code === 'string') : [];
        } catch (error) {
            return [];
        }
    }

    public async leaveUserMode(): Promise<void>
    {
        AuthContext.clear();

        this.clearStorageData();

        this.clearTimer();

        await this.login();
    }

    public async refreshAuth(): Promise<void>
    {
        if (AuthContext.isUserMode()) {
            return;
        }

        if (this.isReloadRequiredForStoredToken()) {
            window.location.reload();

            return;
        }

        try {
            const token = await AuthRestService.getInstance().refreshToken(
                AuthContext.getUserId(),
                AuthContext.getOAuth2TokenOrFail().refreshToken,
            );

            // Another tab can have stored a user-mode token while the refresh was in flight
            if (this.isReloadRequiredForStoredToken()) {
                window.location.reload();

                return;
            }

            AuthContext.setOAuth2Token(token);
            Storage.local.set(
                LocalStorageKeyEnum.OAUTH2_TOKEN,
                JSON.stringify(token)
            );

            this.setRefreshAuthTimer(token);
        } catch (error) {
            alert('Could not refresh authorization, please re-login.');

            await this.login();
        }
    }

    private clearStorageData(): void
    {
        Storage.local.remove(LocalStorageKeyEnum.OAUTH2_TOKEN);
        Storage.local.remove(LocalStorageKeyEnum.CURRENT_CUSTOMER_CODE);
        Storage.local.remove(LocalStorageKeyEnum.CURRENT_PHARMACY_CODE);
        Storage.local.remove(LocalStorageKeyEnum.ID_TOKEN);
        Storage.local.remove(LocalStorageKeyEnum.TOKEN_EXPIRATION);
    }

    /**
     * All tabs share the stored token. A tab follows the token of another tab when user mode is involved,
     * so that an open DP tab never refreshes and writes a DP token over a user-mode token.
     * The storage event only fires in the other tabs, not in the tab that writes.
     */
    private registerStorageListener(): void
    {
        if (this.isStorageListenerRegistered) {
            return;
        }

        window.addEventListener('storage', (event: StorageEvent): void => {
            if (event.key === LocalStorageKeyEnum.OAUTH2_TOKEN && this.isReloadRequiredForStoredToken()) {
                window.location.reload();
            }
        });

        this.isStorageListenerRegistered = true;
    }

    /**
     * True when the stored token differs from the token of this tab and either of them is a user-mode token
     * (a removed stored token counts as different). DP-to-DP changes keep the existing per-tab behaviour.
     */
    private isReloadRequiredForStoredToken(): boolean
    {
        const storedIdToken: string | null = this.getStoredIdToken();

        if (storedIdToken === (AuthContext.oauth2Token?.idToken ?? null)) {
            return false;
        }

        return AuthContext.isUserMode() || this.isUserModeIdToken(storedIdToken);
    }

    private getStoredIdToken(): string | null
    {
        const storedValue = Storage.local.get(LocalStorageKeyEnum.OAUTH2_TOKEN, null);

        if (storedValue === null) {
            return null;
        }

        try {
            const idToken: unknown = JSON.parse(storedValue)?.idToken;

            return typeof idToken === 'string' ? idToken : null;
        } catch (error) {
            return null;
        }
    }

    private isUserModeIdToken(idToken: string | null): boolean
    {
        if (idToken === null) {
            return false;
        }

        try {
            return !!(JwtHelper.parseBodyFromJwt(idToken) as any)['dp:token_exchange'];
        } catch (error) {
            return false;
        }
    }

    private async handleUserModeExpired(): Promise<void>
    {
        alert(I18n.global.t('userModeExpired'));

        await this.leaveUserMode();
    }

    private clearTimer(): void
    {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    private setRefreshAuthTimer(token: OAuth2Token): void
    {
        if (this.timer) {
            clearTimeout(this.timer);
        }

        if (AuthContext.isUserMode()) {
            this.timer = setTimeout(async (): Promise<void> => {
                await this.handleUserModeExpired();
            }, token.expiresAt - new Date().getTime());

            return;
        }

        const runOverMs: number = (token.expiresAt - new Date().getTime()) - 100_000;

        this.timer = setTimeout(async (): Promise<void> => {
            await this.refreshAuth();
        }, runOverMs);
    }
}
